import { AxiosError, type AxiosResponse } from 'axios'

export interface DownloadFileResult {
  blob: Blob
  fileName: string
}

function extractFilenameFromContentDisposition(contentDisposition?: string, fallbackName = 'resultado.zip'): string {
  if (!contentDisposition) return fallbackName

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).replace(/["']/g, '').trim() || fallbackName
    } catch {
      return utf8Match[1].replace(/["']/g, '').trim() || fallbackName
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  if (plainMatch?.[1]) {
    return plainMatch[1].trim() || fallbackName
  }

  return fallbackName
}

export function toDownloadFileResult(response: AxiosResponse<Blob>, fallbackName = 'resultado.zip'): DownloadFileResult {
  const contentDisposition = response.headers['content-disposition'] as string | undefined
  const fileName = extractFilenameFromContentDisposition(contentDisposition, fallbackName)

  return {
    blob: response.data,
    fileName
  }
}

export function triggerDownloadFile(file: DownloadFileResult): void {
  const url = window.URL.createObjectURL(file.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = file.fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

interface ApiErrorPayload {
  message?: string | string[]
}

async function readMessageFromBlob(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text()
    if (!text) return null

    try {
      const parsed = JSON.parse(text) as ApiErrorPayload
      if (Array.isArray(parsed.message) && parsed.message.length > 0) {
        return parsed.message.join(', ')
      }
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return parsed.message
      }
    } catch {
      return text.slice(0, 300)
    }
  } catch {
    return null
  }

  return null
}

export async function extractApiErrorMessage(error: unknown, fallbackMessage: string): Promise<string> {
  if (error instanceof AxiosError) {
    const responseData = error.response?.data

    if (responseData instanceof Blob) {
      const messageFromBlob = await readMessageFromBlob(responseData)
      if (messageFromBlob) return messageFromBlob
    }

    const payload = responseData as ApiErrorPayload | undefined
    if (Array.isArray(payload?.message) && payload.message.length > 0) {
      return payload.message.join(', ')
    }
    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return payload.message
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallbackMessage
}

export function isDownloadFileResult(value: unknown): value is DownloadFileResult {
  if (!value || typeof value !== 'object') return false
  const maybeResult = value as Partial<DownloadFileResult>
  return maybeResult.blob instanceof Blob && typeof maybeResult.fileName === 'string'
}
